import React, { useState, useEffect, useRef } from "react";
import BASE_URL from "../../../assets/global/url";
import { format, parse } from "date-fns";
import { useServerPagination } from "../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../hooks/customHook/paginationHook/usePagination";
import { formatToPHP } from "../../../utils/numberFormatter";

function PurchaseProductTab({ customerId }) {
  const [purchaseData, setPurchaseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);

  // Initialize default pagination
  const pagination = useServerPagination(
    `${BASE_URL}/salesReport/getCustomerLedger/${customerId}`,
    10,
    {},
  );

  // Initialize search pagination
  const searchPagination = useServerPagination(
    `${BASE_URL}/salesReport/getCustomerLedger/${customerId}/search`,
    10,
    {
      searchText: searchTerm,
    },
  );

  // Normalize date format for search
  const normalizeDateForSearch = (input) => {
    if (!input) return input;

    // Try multiple date formats
    const formats = [
      "MMM/dd/yyyy", // Jan/05/2026
      "MM/dd/yyyy", // 01/05/2026
      "yyyy-MM-dd", // 2026-01-05
      "MMM/dd/yy", // Jan/05/26
      "MM/dd/yy", // 01/05/26
      "dd/MM/yyyy", // 05/01/2026
    ];

    for (let fmt of formats) {
      try {
        const parsed = parse(input, fmt, new Date());
        if (!isNaN(parsed)) {
          return format(parsed, "yyyy-MM-dd");
        }
      } catch (e) {
        // Continue to next format
      }
    }

    // If no date format matched, return original input (might be transaction number)
    return input;
  };

  // Transform pagination data to display format
  useEffect(() => {
    const responseData = isSearching ? searchPagination.data : pagination.data;

    // Extract data array - handle both response formats
    const ledgerRows = Array.isArray(responseData)
      ? responseData
      : responseData?.data || [];

    if (ledgerRows && ledgerRows.length > 0) {
      const transformedData = ledgerRows.map((row, index) => ({
        rowKey: `${row.transaction}-${row.createdAt || index}`,
        transactionDate: row.date,
        transactionNumber: row.transaction,
        drNumber: row.displayDR,
        charge: row.charge,
        bank: row.bank,
        checkNo: row.checkNo,
        checkDate: row.checkDate,
        credit: row.credit,
        balance: row.balance,
      }));
      setPurchaseData(transformedData);
    } else {
      setPurchaseData([]);
    }
  }, [pagination.data, searchPagination.data, isSearching]);

  // Debounced search handler
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (value.trim() !== "") {
        setIsSearching(true);
        // Normalize the search term before sending
        const normalizedSearchTerm = normalizeDateForSearch(value.trim());

        // Update search pagination with normalized search term
        searchPagination.updateParams({
          searchText: normalizedSearchTerm,
        });
      } else {
        // Auto clear when search term is empty
        setIsSearching(false);
        pagination.updateParams({});
      }
    }, 500); // 500ms debounce delay
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const isLoading = isSearching ? searchPagination.loading : pagination.loading;
  const currentPagination = isSearching ? searchPagination : pagination;

  return (
    <div>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Transaction Number, DR #, Check #, or Date (e.g., Jan/05/2026, 01/05/2026)..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              <th className="p-2">Date</th>
              <th className="p-2">Transaction</th>
              <th className="p-2">DR #</th>
              <th className="p-2">Charge</th>
              <th className="p-2">Bank</th>
              <th className="p-2">Check #</th>
              <th className="p-2">Check Date</th>
              <th className="p-2">Credit</th>
              <th className="p-2">Balance</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9}>
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : purchaseData.length > 0 ? (
              purchaseData.map((row) => (
                <tr key={row.rowKey}>
                  <td className="p-2">
                    {row.transactionDate
                      ? format(new Date(row.transactionDate), "MMM/dd/yyyy")
                      : "N/A"}
                  </td>
                  <td className="p-2">{row.transactionNumber}</td>
                  <td className="p-2">{row.drNumber || "--"}</td>
                  <td className="p-2">
                    {row.charge ? formatToPHP(row.charge) : ""}
                  </td>
                  <td className="p-2">{row.bank || "--"}</td>
                  <td className="p-2">{row.checkNo || "--"}</td>
                  <td className="p-2">
                    {row.checkDate && row.checkDate !== "--"
                      ? format(new Date(row.checkDate), "MMM/dd/yyyy")
                      : "--"}
                  </td>
                  <td className="p-2">
                    {row.credit ? formatToPHP(row.credit) : ""}
                  </td>
                  <td className="p-2">{formatToPHP(row.balance)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center text-muted p-4">
                  <em>
                    {isSearching && searchTerm.trim() !== ""
                      ? "No results found"
                      : "No purchase data available"}
                  </em>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <PaginationControls {...currentPagination} />
    </div>
  );
}

export default PurchaseProductTab;
